import { OperationVariables, useQuery } from '@apollo/client'
import { CartCount, Customer, LinkProps } from '@interflora/ui-components/build/common/props'
import MainContent from '@interflora/ui-components/build/components/MainContent/MainContent'
import { SiteContext, capitalize } from '@interflora/ui-components/build/utils/common'
import DiscountSuccessModal from '@interflora/ui-components/build/components/DiscountSuccessModal/DiscountSuccessModal'
import axios from 'axios'
// import { useFlags } from 'launchdarkly-react-client-sdk'
import { getSiteKey, useImperativeQuery } from 'lib/apolloClient'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useRef, useContext, useState } from 'react'
import { renderOneTrustTags } from 'scripts/oneTrust'
import { renderSnapchatPixel } from 'scripts/snapchat'
import AnalyticsContext from '../../context/AnalyticsContext'
import { AvailabilityCheckerProvider } from '../../context/AvailabilityCheckerContext'
import { PdpEligibilityProvider } from '@interflora/ui-components/build/utils/PdpEligibilityContext'
import {
  FORMAT_ADDRESS,
  GET_ADDRESS_TOWNS,
  GET_CUSTOMER,
  GET_TOTAL_CONSIGNMENT_COUNT,
  VALIDATE_DISCOUNT_BY_KEY,
} from '../../graphql/queries'
import { Analytics } from '../../lib/Analytics'
import { renderComponents } from '../../utils/components'
import { generateOfflineBreadcrumbs, buildBreadcrumbUrl } from '../../utils/breadcrumbs'
import { getGtmBaseUrl, getGtmId, renderGtmTags } from '../../scripts/gtm'
import { faviconAppleObjects, faviconObjects } from 'common/types'
// Commenting this script import due to DebugBear Security Incident IHD-31473
// import { renderDebugBearTags } from '../../scripts/debugBear'
import {
  enforce_convert_sync_mode,
  enable_zendesk,
  category_pcc,
  enable_google_gateway_tag_implementation,
} from '@interflora/ui-components/build/utils/featureFlags'
import { blogCategories } from '../../constants'
import { useAddressCountiesService } from '../../hooks/useFetchAddressCounties'
import { renderZenDeskScript } from 'scripts/zenDesk'
import FloatingFavoritesButton from '@interflora/ui-components/build/components/FloatingFavoritesButton/FloatingFavoritesButton'
import { shouldRenderFloatingFavoritesButton } from '@interflora/ui-components/build/utils/Favorites'
import { BreadcrumbList, Organization } from 'schema-dts'
import { jsonLdScriptProps } from 'react-schemaorg'
import {
  CONTACT_OPTION_URL_IE,
  CONTACT_OPTION_URL_UK,
  LOGO_URL_UK,
} from '@interflora/ui-components/build/utils/constants'
import { renderPodScribePixel } from 'scripts/podscribe'
import aa from 'search-insights'
import CategoryAvailabilitySection from '../CategoryAvailabilityConfirmation/CategoryAvailabilitySection'

export type OrganizationWithServiceType = Organization & {
  serviceType: string
}

const analytics = new Analytics({ gtmId: getGtmId() })

const convertGTMID = typeof window === 'undefined' ? process.env.GTM_CONVERT_ID : process.env.NEXT_PUBLIC_GTM_CONVERT_ID

/**
 * Check if build number has been updated on server, false if there is an error retrieving the file
 */
const updatedBuildNum = async (buildNum: string) => {
  try {
    const response = await axios.get('/_meta/build.json')
    return buildNum !== response.data?.ssgBuildNum
  } catch (ex) {
    return false
  }
}

/**
 * Helper to determine if category PCC should be shown
 */
const shouldShowCategoryAvailabilityChecker = (
  categoryId: string | undefined,
  isPccEnabled: boolean,
  siteShorthand: string,
  showCheckerForUkFf: boolean,
  showCheckerForIe: boolean
): boolean => {
  if (!categoryId || !isPccEnabled) return false
  if ((siteShorthand === 'uk' || siteShorthand === 'ff') && showCheckerForUkFf) return true
  if (siteShorthand === 'ie' && showCheckerForIe) return true
  return false
}

interface Props {
  renderGraph: any
  disableGtm?: boolean
  disableOneTrust?: boolean
  children: React.ReactNode
  onIsSignedIn?: (customer: Customer) => void
  onIsSignedOut?: () => void
  disableCustomerCache?: boolean
  redirectUrl?: string
  disableDebugbear?: boolean
  disableSnapchatPixel?: boolean
  disablePodScribePixel?: boolean
}

const Page: React.FunctionComponent<any> = (props: Props) => {
  const router = useRouter()
  const { page, site, subject, preloadedComponents, urlInfo, redirectUrl, featureFlags } = props.renderGraph
  const memoizedPreloadedComponents = useMemo(() => preloadedComponents, [preloadedComponents])
  const {
    disableGtm,
    disableOneTrust,
    // commenting due to Debugbear security incident IHD-31473
    // disableDebugbear = false,
    disableSnapchatPixel = false,
    disablePodScribePixel = false,
  } = props
  const formatAddressQuery = useImperativeQuery(FORMAT_ADDRESS)
  const addressTownsQuery = useImperativeQuery(GET_ADDRESS_TOWNS)
  const getFormattedAddress = useCallback(
    (variables: OperationVariables) => formatAddressQuery(variables),
    [formatAddressQuery]
  )
  const getAddressTowns = useCallback(
    (variables: OperationVariables) => addressTownsQuery(variables),
    [addressTownsQuery]
  )
  const {
    [enable_zendesk.key]: enableZenDesk,
    [enable_google_gateway_tag_implementation.key]: enableGoogleGatewayTag,
  } = featureFlags || {}
  const asPath = router?.asPath

  // Discount state
  const validateDiscountQuery = useImperativeQuery(VALIDATE_DISCOUNT_BY_KEY)
  const [discountValidationLoading, setDiscountValidationLoading] = useState(false)
  const [discountValidationCompleted, setDiscountValidationCompleted] = useState(true)
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [discountData, setDiscountData] = useState<any>(null)

  const getCanonicalUrl = () => {
    const pathName = globalThis?.location?.pathname ?? ''
    if (pathName && blogCategories?.includes(pathName)) {
      return globalThis?.location?.href ?? ''
    }
    // Handling canonical url to be the same for category pages as the canonical url is not being returned from the API for category pages
    if (pathName?.includes('/category')) {
      return urlInfo?.canonical ?? globalThis?.location?.href ?? ''
    }
    //All other pages remain untouched
    return urlInfo?.canonical ?? ''
  }

  const breadCrumbComponent = page.components.header?.find(
    (component: { uiType: string }) => component.uiType === 'Breadcrumb'
  )
  const apiBreadCrumbLinks = breadCrumbComponent?.data?.links || []

  const breadCrumbLinks =
    apiBreadCrumbLinks.length > 0 ? apiBreadCrumbLinks : generateOfflineBreadcrumbs(asPath, getCanonicalUrl())

  // Use our custom utility instead of direct imperative query
  const { fetchAddressCounties: fetchCounties } = useAddressCountiesService()
  const fetchAddressCounties = useCallback((country: string) => fetchCounties(country), [fetchCounties])

  const lastCheckedBuild = useRef(Date.now())
  const reloadingPage = useRef(false)
  // Migrated from next/config publicRuntimeConfig (removed in Next.js 16) to process.env
  const buildNum = process.env.SSG_BUILD_NUM || ''
  const refreshTtlSecs = Number(process.env.SSG_REFRESH_TTL_SECS) || 600
  const { isFlyingFlowers } = useContext(SiteContext)

  const amplienceDamDomain = new URL(site.imageUrlPrefix)
  amplienceDamDomain.pathname = ''
  const preconnectDomains = [
    amplienceDamDomain.toString(),
    'https://interflora.a.bigcontent.io',
    'https://cdn.cookielaw.org',
    getGtmBaseUrl(!!enableGoogleGatewayTag),
  ]

  // Feature flags: required for targeting specific users
  // const ldClient = useLDClient()

  const sendUserLoginStatusAnalytics = (loginStatus: string) => {
    const userStatus = sessionStorage.getItem('userStatus')
    if (!userStatus) {
      analytics.userLoginStatusAnalytics(loginStatus)
      sessionStorage.setItem('userStatus', loginStatus)
    }
  }

  const initializeAuthenticatedUserTokenForAlgolia = (customerId?: string) => {
    if (customerId) {
      aa('setAuthenticatedUserToken', customerId)
    }
  }

  const {
    loading: customerLoading,
    data: customerData,
    refetch: customerRefetch,
  } = useQuery<{ customer: Customer }>(GET_CUSTOMER, {
    onCompleted: (data) => {
      if (data?.customer) {
        props.onIsSignedIn && props.onIsSignedIn(data.customer)
        analytics.setUserDetails(data.customer.customerNumber, data.customer.email)
        sendUserLoginStatusAnalytics('signed-in')
        const customerId = data.customer.id
        initializeAuthenticatedUserTokenForAlgolia(customerId)
        // Feature flags: required for targeting specific users
        // ldClient?.identify(
        //   {
        //     kind: 'user',
        //     key: data.customer.customerNumber,
        //     name: [data.customer.firstName, data.customer.lastName].join(' '),
        //     email: data.customer.email,
        //   },
        //   null,
        //   () => { console.log('Updated feature flags for logged in user') }
        // )
      } else {
        props.onIsSignedOut && props.onIsSignedOut()
        analytics.setUserDetails(null, null)
        aa('setAuthenticatedUserToken', undefined)
        sendUserLoginStatusAnalytics('signed-out')
      }
    },
    onError: () => {
      props.onIsSignedOut && props.onIsSignedOut()
      analytics.setUserDetails(null, null)
      aa('setAuthenticatedUserToken', undefined)
    },
    fetchPolicy: props.disableCustomerCache ? 'network-only' : 'cache-first',
    ssr: false,
  })
  const refetchCustomer = useCallback((variables?: any) => customerRefetch(variables), [customerRefetch])
  // Narrowed so SiteContext carries data and a stable refetch, not Apollo's per-render result object
  const getCustomer = useMemo(
    () => ({ loading: customerLoading, data: customerData, refetch: refetchCustomer }),
    [customerLoading, customerData, refetchCustomer]
  )

  const {
    loading: consignmentCountLoading,
    data: consignmentCountData,
    refetch: consignmentCountRefetch,
  } = useQuery<{ cart: CartCount }>(GET_TOTAL_CONSIGNMENT_COUNT, {
    fetchPolicy: 'network-only',
    ssr: false,
  })
  const refetchConsignmentCount = useCallback(() => consignmentCountRefetch(), [consignmentCountRefetch])
  // Stable object so SiteContext only changes when the count result changes
  const getConsignmentCount = useMemo(
    () => ({ loading: consignmentCountLoading, data: consignmentCountData, refetch: refetchConsignmentCount }),
    [consignmentCountLoading, consignmentCountData, refetchConsignmentCount]
  )

  analytics.currency = props.renderGraph.currency
  analytics.subject = subject

  /*
   * When the route changes, we need to issue a 'page_view' event
   * to Google Analytics. The 'routeChangeComplete' fires after the
   * data for the page has loaded, and after the <title> tag value
   * has been changed, meaning that we're able to access the latest
   * value for the document title. We fire the `page_view` event,
   * which is a standard enhanced measurement event:
   * https://support.google.com/analytics/answer/9216061
   */
  useEffect(() => {
    const handlePageChangeStart = async (url: string) => {
      reloadingPage.current = false
      !disableGtm && analytics.bufferEvents()

      // Check if we need to recheck the build number after TTL seconds
      const checkBuildNum = Date.now() - lastCheckedBuild.current > refreshTtlSecs * 1000

      // Only recheck if we're not in the checkout flow
      if (checkBuildNum && !url?.includes('/checkout')) {
        if (await updatedBuildNum(buildNum)) {
          // Redirect to the url and cancel the analytic events
          reloadingPage.current = true
          window.location.assign(url)
        } else {
          // Update the time build was last checked
          lastCheckedBuild.current = Date.now()
        }
      }
    }

    const handlePageChangeComplete = () => {
      if (!disableGtm && !reloadingPage.current) {
        // Update dataLayer directly on route change instead of firing analytics event

        analytics.viewPage({
          page_title: document.title,
        })

        analytics.flushEvents()
      }
    }

    router.events.on('routeChangeStart', handlePageChangeStart)
    router.events.on('routeChangeComplete', handlePageChangeComplete)
    return () => {
      router.events.off('routeChangeStart', handlePageChangeStart)
      router.events.off('routeChangeComplete', handlePageChangeComplete)
    }
  }, [disableGtm, router.events, buildNum, refreshTtlSecs])

  // Handle discount key from URL query parameters
  useEffect(() => {
    if (typeof globalThis === 'undefined' && !router.isReady) return

    const params = new URLSearchParams(globalThis.location.search)
    const ctdKey = params.get('ctdKey')

    if (ctdKey) {
      localStorage.setItem('ctdKey', ctdKey)
      // Remove query param from URL using History API
      const newUrl = globalThis.location.pathname + globalThis.location.hash
      globalThis.history.replaceState({ path: newUrl }, '', newUrl)

      // Set loading state
      setDiscountValidationLoading(true)
      setDiscountValidationCompleted(false)

      // Validate discount code
      validateDiscountQuery({ ctdKey })
        .then((result) => {
          const discount = result?.data?.validateDiscountCode
          if (discount.isValid && discount.discountCode?.code) {
            // Check if discount is active
            // Only store discount code in localStorage if discount is active
            // Replace any existing discount code with the new one
            const couponData = {
              code: discount.discountCode?.code,
              description: discount.discountCode?.description,
              name: discount.discountCode?.name,
              ctdKey: discount.discountCode?.key,
              isApplied: true,
            }
            localStorage.setItem('discountByUrl', JSON.stringify(couponData))
            setDiscountData(couponData)
            setShowDiscountModal(true)
            //Adding the below two to display the modal and banner at the same time
            setDiscountValidationLoading(false)
            setDiscountValidationCompleted(true)
          } else {
            localStorage.removeItem('discountByUrl')
          }
        })
        .finally(() => {
          // Set loading complete
          setDiscountValidationLoading(false)
          setDiscountValidationCompleted(true)
        })
    } else {
      // No ctdKey, so validation is complete immediately
      setDiscountValidationLoading(false)
      setDiscountValidationCompleted(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  // Check client side ABTesting is enabled and add it to server side feature flags for passing to the SiteContext
  // const { aBTestingClient } = useFlags()
  // const isABTestingEnabled = !!featureFlags?.['a-b-testing'] && aBTestingClient
  const convertSyncModeFlag = featureFlags?.[enforce_convert_sync_mode.key]

  const isConvertSyncModeEnabled =
    (convertSyncModeFlag &&
      typeof convertSyncModeFlag === 'object' &&
      Object.keys(convertSyncModeFlag).length > 0 &&
      (convertSyncModeFlag?.RegExs || []).some((pattern: string) => new RegExp(pattern)?.test(router.asPath))) ||
    false

  const isABTestingEnabled = !!featureFlags?.['a-b-testing']
  const clientFeatureFlags = useMemo(
    () => ({ ...featureFlags, isABTestingEnabled, convertTagMode: isConvertSyncModeEnabled }),
    [featureFlags, isABTestingEnabled, isConvertSyncModeEnabled]
  )

  // taking the value from subject as "includeProductInSitemap" boolean only required for product pages
  const includeProductInSitemap = subject?.product?.includeProductInSitemap

  const displayCanonicalTag = (pageInfo: any) => {
    let displayCanonical = false
    if (
      pageInfo?.seo?.robotsControl?.length === 0 ||
      pageInfo?.seo?.robotsControl?.includes('noindex') === false ||
      pageInfo?.seo?.noIndex === false
    ) {
      displayCanonical = true
    }
    return displayCanonical
  }

  const showFloatingFavoritesButton = useMemo(
    () =>
      shouldRenderFloatingFavoritesButton({
        asPath,
        isFlyingFlowers: isFlyingFlowers ?? false,
        subject,
      }),
    [isFlyingFlowers, asPath, subject]
  )

  // Category PCC: show AvailabilityChecker at top of category pages when LD flag + Amplience switch are enabled
  const productListingComponentData = page.components.body?.find((c: any) => c.uiType === 'ProductListing')?.data
  const isCategoryPccEnabled = !!featureFlags?.[category_pcc.key]
  const showCategoryAvailabilityChecker = shouldShowCategoryAvailabilityChecker(
    subject?.category?.id,
    isCategoryPccEnabled,
    site.shorthand,
    !!productListingComponentData?.showCategoryAvailabilityChecker,
    !!productListingComponentData?.showCategoryAvailabilityCheckerIE
  )

  // Memoized so SiteContext consumers only re-render when a listed dependency actually changes
  const siteContextValue = useMemo(
    () => ({
      ...site,
      site: getSiteKey(),
      getCustomer,
      getFormattedAddress,
      preloadedComponents: memoizedPreloadedComponents,
      getAddressTowns,
      fetchAddressCounties,
      isFlyingFlowers: site.shorthand === 'ff',
      subject,
      getConsignmentCount,
      featureFlags: clientFeatureFlags,
      discountValidationLoading,
      discountValidationCompleted,
    }),
    [
      site,
      getCustomer,
      getFormattedAddress,
      memoizedPreloadedComponents,
      getAddressTowns,
      fetchAddressCounties,
      subject,
      getConsignmentCount,
      clientFeatureFlags,
      discountValidationLoading,
      discountValidationCompleted,
    ]
  )

  return (
    <>
      {redirectUrl ? (
        <>
          <Head>
            <meta httpEquiv="refresh" content={`0;URL='${redirectUrl}'`} />
          </Head>
          <main>
            {!props.children && (
              <>
                {renderComponents(page.components.header)}
                {renderComponents(page.components.footer)}
              </>
            )}
            {props.children}
          </main>
        </>
      ) : (
        <>
          <Head>
            <script
              src={`https://cdn-4.convertexperiments.com/v1/js/${convertGTMID}.js?environment=production`}
              {...(!isConvertSyncModeEnabled && { async: true })}
            />
            {preconnectDomains?.map((domain) => (
              <link rel="preconnect" href={domain} key={domain.replace('https://', '')} />
            ))}

            {site.shorthand !== 'ff' &&
              faviconObjects?.map((logo) =>
                logo.logoName ? (
                  <link
                    key={`${logo.logoName}${logo.sizes}`}
                    rel={'icon'}
                    type="image/png"
                    sizes={logo.sizes}
                    href={`https://media.interflora.co.uk/i/interflora/${logo.logoName}`}
                  />
                ) : (
                  ''
                )
              )}
            {site.shorthand !== 'ff' &&
              faviconAppleObjects?.map((applelogo) =>
                applelogo.logoName ? (
                  <link
                    key={`${applelogo.logoName}${applelogo.sizes}`}
                    rel={'apple-touch-icon'}
                    type="image/png"
                    sizes={applelogo.sizes}
                    href={`https://media.interflora.co.uk/i/interflora/${applelogo.logoName}`}
                  />
                ) : (
                  ''
                )
              )}

            {site.shorthand === 'ff' && (
              <>
                <link rel="apple-touch-icon" href="/images/apple-touch-icon.png" />
                <link rel="icon" type="image/png" href="/images/favicon.ico" />
              </>
            )}

            {page?.seo?.title && <title>{page.seo.title}</title>}
            {page?.seo?.metaDescription && <meta name="description" content={page.seo.metaDescription} />}
            {page?.seo?.lastModified && <meta name="lastModified" content={page.seo.lastModified} hidden />}
            {includeProductInSitemap === false && <meta name="includeProductInSitemap" content="false" hidden />}
            {!!page.seo?.robotsControl?.length && <meta name="robots" content={page.seo?.robotsControl.join(', ')} />}
            {!!redirectUrl && <meta httpEquiv="refresh" content={`0;URL='${redirectUrl}'`} />}
            {displayCanonicalTag(page) && urlInfo?.canonical && <link rel="canonical" href={getCanonicalUrl()} />}
            {urlInfo?.alternate?.map((alternateUrl: { hreflang: string; href: string }) => (
              <link
                key={alternateUrl.hreflang}
                rel="alternate"
                hrefLang={alternateUrl.hreflang}
                href={alternateUrl.href}
              />
            ))}
            {breadCrumbLinks.length > 0 && (
              <script
                type="application/ld+json"
                {...(jsonLdScriptProps<BreadcrumbList>({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: breadCrumbLinks?.map((link: LinkProps, index: number) => {
                    const itemUrl = buildBreadcrumbUrl(link, index, breadCrumbLinks, asPath)
                    const finalItem = `https://${site.canonicalHostname}${itemUrl}`
                    return {
                      '@type': 'ListItem',
                      position: index + 1,
                      name: link.label,
                      item: finalItem,
                    }
                  }),
                }) as React.HTMLProps<HTMLScriptElement>)}
              />
            )}
            {!isFlyingFlowers && asPath.startsWith('/flower-delivery/') && (
              <script
                {...(jsonLdScriptProps<OrganizationWithServiceType>({
                  '@context': 'https://schema.org',
                  '@type': 'Organization',
                  name: 'Interflora',
                  url: urlInfo.canonical,
                  logo: LOGO_URL_UK,
                  sameAs: [
                    'https://www.facebook.com/InterfloraUK',
                    'https://www.instagram.com/interflora_uk',
                    'https://twitter.com/InterfloraUK',
                  ],
                  areaServed: {
                    '@type': 'AdministrativeArea',
                    name: capitalize(router.asPath.split('/').findLast(Boolean) ?? '') || '',
                    address: {
                      '@type': 'PostalAddress',
                      addressLocality: capitalize(router.asPath.split('/').findLast(Boolean) ?? '') || '',
                      addressCountry: site.shorthand === 'ie' ? 'IE' : 'GB',
                    },
                  },
                  serviceType: 'Same-day Flower Delivery',
                  contactPoint: [
                    {
                      '@type': 'ContactPoint',
                      contactType: 'customer service',
                      availableLanguage: ['English'],
                      url: site.shorthand === 'ie' ? CONTACT_OPTION_URL_IE : CONTACT_OPTION_URL_UK,
                      contactOption: site.shorthand === 'ie' ? CONTACT_OPTION_URL_IE : CONTACT_OPTION_URL_UK,
                    } as any,
                  ],
                  foundingDate: '1923-01-01',
                  mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': urlInfo.canonical,
                  },
                }) as React.HTMLProps<HTMLScriptElement>)}
              />
            )}
          </Head>
          {!disableOneTrust && renderOneTrustTags()}
          {!disableSnapchatPixel && site.shorthand === 'uk' && renderSnapchatPixel()}
          {!disablePodScribePixel && site.shorthand.toLowerCase() === 'uk' && renderPodScribePixel()}

          {!disableGtm && renderGtmTags({ isABTestingEnabled, enableGateway: !!enableGoogleGatewayTag })}
          {/* Commenting this script import due to DebugBear Security Incident  IHD-31473 */}
          {/* {!disableDebugbear && renderDebugBearTags()} */}
          {enableZenDesk && renderZenDeskScript(site.shorthand)}
          <main>
            <AnalyticsContext.Provider value={analytics}>
              <SiteContext.Provider value={siteContextValue}>
                {!props.children && (
                  <>
                    {renderComponents(page.components.header)}
                    <PdpEligibilityProvider>
                      <AvailabilityCheckerProvider>
                        <MainContent
                          centreContent={page.centreContent}
                          isFloatingFavoritesEnabled={showFloatingFavoritesButton}
                        >
                          {showFloatingFavoritesButton && <FloatingFavoritesButton />}
                          {showCategoryAvailabilityChecker && (
                            <div data-testid="category-availability-section-wrapper">
                              <CategoryAvailabilitySection
                                occasionQuickLinks={productListingComponentData?.occasionQuickLinks}
                              />
                            </div>
                          )}
                          {renderComponents(
                            page.components.body?.map((component: any) => {
                              if (component.uiType === 'ProductDetail') {
                                return {
                                  ...component,
                                  data: {
                                    ...component.data,
                                    messageCopy: page.messageCopy,
                                    isKlarnaEnabledInPdp: page.isKlarnaEnabledInPdp,
                                    isPayPalEnabledInPdp: page.isPayPalEnabledInPdp,
                                  },
                                }
                              }
                              return component
                            })
                          )}
                        </MainContent>
                      </AvailabilityCheckerProvider>
                      {renderComponents(page.components.footer)}
                    </PdpEligibilityProvider>
                  </>
                )}
                {props.children}
              </SiteContext.Provider>
            </AnalyticsContext.Provider>
          </main>
          <DiscountSuccessModal
            open={showDiscountModal}
            onClose={() => setShowDiscountModal(false)}
            discountDetails={discountData}
          />
        </>
      )}
    </>
  )
}

export default Page
